import { IUserExperience, UserExperienceState, UserExperienceType, UserExperienceSubtype } from "common/interfaces";
import { userExperiences as userExperiencesApi } from "apis-v2/callables";
import { sessionId, browserId } from "js/core/browserAndSession";
import { auth } from "js/firebase/auth";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { getSequentialRunner } from "common/utils/runSequentially";
import { releaseVersion } from "js/config";

const logger = getLogger(LogGroup.USER_EXPERIENCES);

const runSequentially = getSequentialRunner();

let currentExperience: IUserExperience | null = null;

function getMetadata(): IUserExperience["metadata"] {
    return {
        sessionId,
        browserId,
        uid: auth().currentUser?.uid,
        workspaceId: AppController.workspaceId,
        releaseVersion,
    };
}

function updateCurrentExperience({
    type,
    state,
    metadata,
    condition,
    reregister
}: {
    type: UserExperienceType | UserExperienceType[];
    state?: UserExperienceState;
    metadata: Partial<IUserExperience["metadata"]>;
    condition?: (metadata: IUserExperience["metadata"]) => boolean;
    reregister?: boolean;
}) {
    runSequentially(async () => {
        if (reregister && Array.isArray(type)) {
            throw new Error("Cannot reregister for multiple user experience types");
        }

        if (!currentExperience) {
            return;
        }

        if (currentExperience.state !== UserExperienceState.STARTED) {
            return;
        }

        if (Array.isArray(type) ? !type.includes(currentExperience.type) : currentExperience.type !== type) {
            return;
        }

        if (condition && !condition(currentExperience.metadata)) {
            return;
        }

        try {
            currentExperience = await userExperiencesApi.update({
                id: currentExperience.id,
                metadata: {
                    ...metadata,
                    ...getMetadata()
                },
                ...(state ? { state } : {})
            });

            if (reregister && state === UserExperienceState.FAILED) {
                currentExperience = await userExperiencesApi.register({
                    type: type as UserExperienceType,
                    metadata: {
                        ...currentExperience.metadata,
                        ...metadata,
                        ...getMetadata()
                    }
                });
            }
        } catch (err) {
            logger.error(err, "updateCurrentExperience() failed to update current user experience", { type, state });
        }
    });
}

/**
 * Registers a new user experience, if there is a started current experience of the same type, it will be kept and updated with the new metadata.
 */
export function register(type: UserExperienceType, metadata?: Partial<IUserExperience["metadata"]>) {
    runSequentially(async () => {
        try {
            if (currentExperience && currentExperience.type === type && currentExperience.state === UserExperienceState.STARTED) {
                currentExperience = await userExperiencesApi.update({
                    id: currentExperience.id,
                    metadata: {
                        ...metadata,
                        ...getMetadata()
                    }
                });

                return;
            }

            currentExperience = await userExperiencesApi.register({
                type,
                metadata: {
                    ...metadata,
                    ...getMetadata()
                }
            });
        } catch (err) {
            logger.error(err, "register() failed to register user experience", { type });

            currentExperience = null;
        }
    });
}

/**
 * Completes the current user experience if it has the same type and is started.
 */
export function complete(type: UserExperienceType | UserExperienceType[], metadata?: Partial<IUserExperience["metadata"]>, condition?: (metadata: IUserExperience["metadata"]) => boolean) {
    updateCurrentExperience({
        type,
        state: UserExperienceState.COMPLETED,
        metadata,
        condition
    });
}

/**
 * Abandons the current user experience if it has the same type and is started.
 */
export function abandon(type: UserExperienceType | UserExperienceType[], metadata?: Partial<IUserExperience["metadata"]>, condition?: (metadata: IUserExperience["metadata"]) => boolean) {
    updateCurrentExperience({
        type,
        state: UserExperienceState.ABANDONED,
        metadata,
        condition
    });
}

/**
 * Fails the current user experience if it has the same type and is started.
 */
export function fail(type: UserExperienceType | UserExperienceType[], metadata?: Partial<IUserExperience["metadata"]>, condition?: (metadata: IUserExperience["metadata"]) => boolean, reregister?: boolean) {
    updateCurrentExperience({
        type,
        state: UserExperienceState.FAILED,
        metadata,
        condition,
        reregister
    });
}

/**
 * Updates the current user experience with new metadata if it has the same type and is started.
 */
export function update(type: UserExperienceType | UserExperienceType[], metadata?: Partial<IUserExperience["metadata"]>, condition?: (metadata: IUserExperience["metadata"]) => boolean) {
    updateCurrentExperience({
        type,
        metadata,
        condition
    });
}

export { UserExperienceType as Type, UserExperienceState as State, UserExperienceSubtype as Subtype };
